import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translations from '@translationKeys'

import LegalFooter from '@components/LegalFooter'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { FetchEventsResponse, POneEvent } from '@modules/hub/types'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'

import hubApi from './api'
import PastEventsPopup from './components/PastEventsPopup'
import PastEventsPopupMobile from './components/PastEventsPopupMobile'
// import GeneralSection from './components/GeneralSection'
// import HelpSection from './components/HelpSection'
import TopSection, { DEFAULT_EVENT_PAGE_LIMIT } from './components/TopSection'

const HubPage = () => {
  const { t } = useTranslation()

  const { user } = useAuthContext()

  const [pastEventsCarouselShown, setPastEventsCarouselShown] = useState<boolean>(false)

  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    const originalTitle = document.title
    document.title = 'partneringONE hub'
    return () => {
      document.title = originalTitle
    }
  }, [])
  const { data: pastEventsData, fetchNextPage: fetchNextPastEventsPage } = useInfiniteQuery<FetchEventsResponse>({
    queryKey: [hubApi.fetchEvents.key, user?.id, 'past'],
    queryFn: ({ pageParam }) =>
      hubApi.fetchEvents.fn({
        personId: user?.id as number,
        type: 'past',
        limit: DEFAULT_EVENT_PAGE_LIMIT,
        page: (pageParam as number) || 1,
      }),

    getNextPageParam: (lastPage, allPages) => {
      const pagesLoadedCount = allPages.map(page => page.rows.length).reduce((acc, value) => acc + value, 0)

      if (pagesLoadedCount < lastPage.count) {
        return pagesLoadedCount / DEFAULT_EVENT_PAGE_LIMIT + 1
      }

      return undefined
    },
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    enabled: Boolean(user?.id),
  })

  const getEventErrorMessage = (event: POneEvent): string | undefined => {
    if (!event) {
      return undefined
    }

    if (event.isDelegateBlocked) {
      return t(translations.SORRY_YOUR_ACCOUNT_FOR_THIS_EVENT_IS_BLOCKED) as string
    }

    return undefined
  }

  const pastEvents = (pastEventsData?.pages ?? [])
    .reduce((acc, i) => [...acc, ...i.rows], [] as POneEvent[])
    .map(event => ({ ...event, error: getEventErrorMessage(event) }))

  const renderPopup = () => {
    if (isMobile) {
      return (
        <PastEventsPopupMobile
          eventsList={pastEvents}
          onClose={() => {
            setPastEventsCarouselShown(false)
          }}
        />
      )
    }
    return (
      <PastEventsPopup
        eventsList={pastEvents}
        onClose={() => {
          setPastEventsCarouselShown(false)
        }}
      />
    )
  }

  return (
    <Box overflow={pastEventsCarouselShown ? 'hidden' : 'auto'}>
      {pastEventsCarouselShown && pastEvents?.length ? renderPopup() : null}
      <TopSection
        setPastEventsCarouselShown={setPastEventsCarouselShown}
        fetchNextPastEventsPage={fetchNextPastEventsPage}
        pastEvents={pastEvents}
      />
      {/* <GeneralSection /> */}
      {/* <HelpSection /> */}
      <LegalFooter withAdditionalPadding />
    </Box>
  )
}

export default HubPage
